import { useMemo } from 'react';
import {
  PageShell, PageContent,
  Button, Alert, StatGrid, Stat,
} from '@00start/ui';
import { useDemoRun } from '../hooks/useDemoRun';
import { StageCard } from '../components/StageCard';
import { KeyValueGrid } from '../components/KeyValueGrid';
import { FindingsTable } from '../components/FindingsTable';
import { BrandStrip } from '../components/BrandStrip';

export function DemoPage() {
  const {
    inspectionId, stage1, stage2, stage3, isRunning, run,
  } = useDemoRun();

  const stats = useMemo(() => {
    const findings = stage3.data?.allFindings ?? [];
    const uniqueCitations = new Set(
      findings.map(f => f.compliance?.codeSection).filter(Boolean),
    ).size;
    const compliant = findings.filter(f => f.compliance?.compliant).length;
    return { findings: findings.length, uniqueCitations, compliant };
  }, [stage3.data]);

  return (
    <PageShell
      breadcrumbs={<BrandStrip context="Aviation • FAA Part 145" />}
      title="AVIATION COMPLIANCE"
      subtitle="FAA Part 145 end-to-end evidence — live against 00start staging"
      action={
        <div className="flex gap-2">
          <Button
            onClick={() => { window.location.hash = '#chat'; }}
            data-testid="aviation-demo-chat-link"
          >
            Try Chat →
          </Button>
          <Button
            onClick={run}
            disabled={isRunning}
            data-testid="aviation-demo-run-button"
          >
            {isRunning ? 'Running…' : 'Run Demo'}
          </Button>
        </div>
      }
    >
      <PageContent>
        <div
          className="grid gap-4"
          data-testid="aviation-demo-root"
        >
          <StatGrid columns={3}>
            <Stat
              label="Findings"
              value={stats.findings > 0 ? stats.findings : '—'}
            />
            <Stat
              label="Unique Citations"
              value={stats.uniqueCitations > 0 ? stats.uniqueCitations : '—'}
            />
            <Stat
              label="Compliant"
              value={stats.findings > 0 ? `${stats.compliant} / ${stats.findings}` : '—'}
            />
          </StatGrid>

          {/* Stage 1 — Health */}
          <StageCard
            stage={1}
            title="Aviation RAG Health Probe"
            status={stage1.status}
            error={stage1.error}
          >
            {stage1.data ? (
              <KeyValueGrid
                items={[
                  { key: 'service',  value: stage1.data.service,  mono: true, testId: 'aviation-demo-stage-1-service' },
                  { key: 'status',   value: stage1.data.status,   mono: true, testId: 'aviation-demo-stage-1-status-body' },
                  { key: 'endpoint', value: '/api/compliance-check (GET → upstream /health)', mono: true },
                ]}
              />
            ) : (
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Click <span className="font-medium">Run Demo</span> to probe the live staging aviation-code-rag worker.
              </p>
            )}
          </StageCard>

          {/* Stage 2 — Direct RAG call */}
          <StageCard
            stage={2}
            title="Direct Compliance Check — FAA Part 145 Airframe"
            status={stage2.status}
            error={stage2.error}
          >
            {stage2.data ? (
              <div className="space-y-3">
                <KeyValueGrid
                  items={[
                    { key: 'method',        value: stage2.data.method,        mono: true, testId: 'aviation-demo-stage-2-method' },
                    { key: 'code section',  value: stage2.data.code_section,  mono: true, testId: 'aviation-demo-stage-2-code' },
                    { key: 'jurisdiction',  value: stage2.data.jurisdiction,  mono: true, testId: 'aviation-demo-stage-2-jurisdiction' },
                    { key: 'compliant',     value: String(stage2.data.compliant), testId: 'aviation-demo-stage-2-compliant' },
                    { key: 'confidence',    value: stage2.data.confidence,    testId: 'aviation-demo-stage-2-confidence' },
                  ]}
                />
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1">
                    requirement
                  </div>
                  <p className="text-sm text-gray-700 dark:text-slate-300">
                    {stage2.data.requirement}
                  </p>
                </div>
                {stage2.data.remediation && stage2.data.remediation !== 'No action required.' && (
                  <Alert variant="info" title="Remediation">
                    {stage2.data.remediation}
                  </Alert>
                )}
                {stage2.data.sources?.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1">
                      sources
                    </div>
                    <ul className="text-sm text-gray-700 dark:text-slate-300 list-disc list-inside space-y-1">
                      {stage2.data.sources.map((s) => <li key={s}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Awaiting Stage 1…
              </p>
            )}
          </StageCard>

          {/* Stage 3 — Full compiled report */}
          <StageCard
            stage={3}
            title="Compiled Report — End-to-End"
            status={stage3.status}
            error={stage3.error}
          >
            {stage3.data ? (
              <div className="space-y-3">
                <KeyValueGrid
                  items={[
                    { key: 'inspection', value: stage3.data.inspectionId, mono: true, testId: 'aviation-demo-stage-3-inspection' },
                    { key: 'protocol',   value: stage3.data.protocolId,    mono: true, testId: 'aviation-demo-stage-3-protocol' },
                    { key: 'address',    value: stage3.data.address,       testId: 'aviation-demo-stage-3-address' },
                  ]}
                />
                <FindingsTable findings={stage3.data.allFindings} />
                {stage3.data.complianceFootnote && (
                  <Alert variant="info">{stage3.data.complianceFootnote}</Alert>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Awaiting Stage 2…
              </p>
            )}
          </StageCard>

          {/* Pinned inspection note — gives the reviewer immediate context
              about why they're seeing a fixed ID. Public visibility is
              intentional; no credentials or PII are exposed. */}
          <p
            className="text-xs text-gray-500 dark:text-slate-400 text-center"
            data-testid="aviation-demo-inspection-note"
          >
            pinned to synthetic inspection <span className="font-mono">{inspectionId}</span>
          </p>
        </div>
      </PageContent>
    </PageShell>
  );
}
